
import { Drop, Auction, Raffle } from '../domain/types';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { CacheInvalidator } from '../utils/cacheInvalidation';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  setDoc,
  runTransaction,
  query,
  limit,
  orderBy
} from "firebase/firestore";

function isIgnoredFirestoreError(error: unknown): boolean {
    if (error instanceof Error) {
        const message = error.message || '';
        return message.includes('permission-denied') ||
               message.includes('Null value error') ||
               message.includes('emulator') ||
               message.includes('INTERNAL ASSERTION FAILED');
    }
    return false;
}

class ClubRepository {
    private dropsColl = 'drops';
    private auctionsColl = 'auctions';
    private rafflesColl = 'raffles';

  async getDrops(): Promise<Drop[]> {
    try {
      const q = query(collection(db, this.dropsColl), limit(50), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Drop));
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error fetching drops', error);
      }
      return [];
    }
  }

  async addDrop(drop: Drop): Promise<void> {
    try {
      if (drop.id) {
        await setDoc(doc(db, this.dropsColl, drop.id), drop);
      } else {
        await addDoc(collection(db, this.dropsColl), drop);
      }
      CacheInvalidator.invalidateClub();
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error adding drop', error);
      }
    }
  }

  async updateDrop(updatedDrop: Drop): Promise<void> {
    try {
      await updateDoc(doc(db, this.dropsColl, updatedDrop.id), { ...updatedDrop });
      CacheInvalidator.invalidateClub();
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error updating drop', error);
      }
    }
  }

  async deleteDrop(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.dropsColl, id));
      CacheInvalidator.invalidateClub();
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error deleting drop', error);
      }
    }
  }

    async deleteAllDrops(): Promise<void> {
        try {
            const snapshot = await getDocs(collection(db, this.dropsColl));
            const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);
        } catch (error) {
            if (!isIgnoredFirestoreError(error)) {
                logger.error('[ClubRepository] Error deleting all drops', error);
            }
        }
    }

    async addDrops(drops: Drop[]): Promise<void> {
        for (const drop of drops) {
            await this.addDrop(drop);
        }
    }

    async getAuctions(): Promise<Auction[]> {
        try {
            const snapshot = await getDocs(collection(db, this.auctionsColl));
            return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Auction));
        } catch (error) {
            if (!isIgnoredFirestoreError(error)) {
                logger.error('[ClubRepository] Error fetching auctions', error);
            }
            return [];
        }
    }

  async addAuction(auction: Auction): Promise<void> {
    try {
      if (auction.id) {
        await setDoc(doc(db, this.auctionsColl, auction.id), auction);
      } else {
        await addDoc(collection(db, this.auctionsColl), auction);
      }
      CacheInvalidator.invalidateClub();
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error adding auction', error);
      }
    }
  }

  async updateAuction(updatedAuction: Auction): Promise<void> {
    try {
      await updateDoc(doc(db, this.auctionsColl, updatedAuction.id), { ...updatedAuction });
      CacheInvalidator.invalidateClub();
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error updating auction', error);
      }
    }
  }

  async deleteAuction(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.auctionsColl, id));
      CacheInvalidator.invalidateClub();
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error deleting auction', error);
      }
    }
  }

    async deleteAllAuctions(): Promise<void> {
        try {
            const snapshot = await getDocs(collection(db, this.auctionsColl));
            const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);
        } catch (error) {
            if (!isIgnoredFirestoreError(error)) {
                logger.error('[ClubRepository] Error deleting all auctions', error);
            }
        }
    }

    async addAuctions(auctions: Auction[]): Promise<void> {
        for (const auction of auctions) {
            await this.addAuction(auction);
        }
    }

    async placeBid(auctionId: string, amount: number, user: string): Promise<{ success: boolean; message?: string }> {
        const auctionRef = doc(db, this.auctionsColl, auctionId);
        
        try {
            return await runTransaction(db, async (transaction) => {
                const auctionDoc = await transaction.get(auctionRef);
                if (!auctionDoc.exists()) {
                    return { success: false, message: 'La subasta no existe' };
                }

                const data = auctionDoc.data() as Auction;
                
                // VALIDATION 1: Check if auction is active
                if (!data.isActive) {
                    return { success: false, message: 'La subasta está pausada' };
                }

                // VALIDATION 2: Check if auction has ended
                const endsAt = new Date(data.endsAt);
                if (endsAt < new Date()) {
                    return { success: false, message: 'La subasta ha finalizado' };
                }

                // VALIDATION 3: Check if auction is closed
                if (data.status === 'closed' || data.status === 'paid') {
                    return { success: false, message: 'La subasta ya está cerrada' };
                }

                // VALIDATION 4: Check bid amount
                if (amount <= data.currentBid) {
                    return { success: false, message: `La puja debe ser mayor a $${data.currentBid.toLocaleString()}` };
                }

                const newBid = { user, amount, timestamp: new Date().toISOString() };
      transaction.update(auctionRef, {
        currentBid: amount,
        highestBidder: user,
        bids: [newBid, ...(data.bids || [])]
      });
      CacheInvalidator.invalidateClub();
      return { success: true };
            });
        } catch (e) {
            if (!isIgnoredFirestoreError(e)) {
                logger.error('Bid transaction failed', e instanceof Error ? e : new Error(String(e)));
            }
            return { success: false, message: 'Error de conexión' };
        }
    }

  async closeAuction(auctionId: string): Promise<void> {
    try {
      const auctionRef = doc(db, this.auctionsColl, auctionId);
      const auctionDoc = await getDoc(auctionRef);
      if (auctionDoc.exists()) {
        const data = auctionDoc.data() as Auction;
        await updateDoc(auctionRef, {
          status: 'closed',
          winner: data.highestBidder
        });
      }
      CacheInvalidator.invalidateClub();
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error closing auction', error);
      }
    }
  }

    async getActiveRaffle(): Promise<Raffle | null> {
        try {
            const docSnap = await getDoc(doc(db, this.rafflesColl, 'active'));
            return docSnap.exists() ? (docSnap.data() as Raffle) : null;
        } catch (error) {
            if (!isIgnoredFirestoreError(error)) {
                logger.error('[ClubRepository] Error fetching active raffle', error);
            }
            return null;
        }
    }

  async updateRaffle(updatedRaffle: Partial<Raffle>): Promise<void> {
    try {
      await setDoc(doc(db, this.rafflesColl, 'active'), updatedRaffle, { merge: true });
      CacheInvalidator.invalidateClub();
    } catch (error) {
      if (!isIgnoredFirestoreError(error)) {
        logger.error('[ClubRepository] Error updating raffle', error);
      }
    }
  }
}

export const clubRepository = new ClubRepository();
